/**
 * BUG 425 — per-user, per-working-day UI preferences (filters, toggles,
 * sort, view modes) that survive page navigation, reloads and other tabs.
 *
 * React Router unmounts a page on navigation, so anything held in plain
 * `useState` is lost the moment staff open another screen. This module is the
 * storage half of `hooks/usePersistedState`; it is pure (no React) so
 * AuthContext can clear preferences on logout without importing a hook.
 *
 * ── Key ───────────────────────────────────────────────────────────────────
 *   ui:<userId>:<page>:<option>     e.g. ui:66f…:manifest:showExpiredLoads
 * Scoped to the authenticated user so one account never sees another's
 * settings on a shared workstation. No user id → nothing is persisted.
 *
 * ── Value (envelope) ──────────────────────────────────────────────────────
 *   { v: <value>, d: 'YYYY-MM-DD', tz: '<IANA tz>' }
 * `d` is the DROPZONE's calendar day when the value was written, `tz` the
 * zone it was measured in. A read compares `d` with today in that same `tz`,
 * so expiry never depends on whether SystemConfig has loaded yet (a cold
 * start reports 'UTC', which would otherwise "expire" a Toronto evening's
 * settings five hours early). Anything from another day, unparseable, or of
 * the wrong type is removed and the caller's default applies.
 *
 * ── Lifecycle ─────────────────────────────────────────────────────────────
 *   navigation / reload / second tab, same user, same DZ day → kept
 *   next DZ working day                                        → expired
 *   logout (or session expiry)                                 → cleared
 *
 * ONLY UI preferences belong here — never tokens, form submissions, dialog
 * state or customer records. Every storage access is wrapped: a browser with
 * storage disabled (private mode, quota, policy) degrades to in-memory state.
 */
import { todayInDz } from './timezone';

export const UI_PREF_PREFIX = 'ui:';

/** Longest string value persisted (search boxes); longer input is not saved. */
export const MAX_PERSISTED_STRING = 200;

function storage() {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch (_) {
    return null; // access itself can throw (blocked site data)
  }
}

export function buildPrefKey(userId, prefKey) {
  if (!userId || !prefKey) return null;
  return `${UI_PREF_PREFIX}${String(userId)}:${prefKey}`;
}

/** Today's DZ-local day in `tz`; '' when the zone is unusable. */
export function dzDayFor(tz) {
  try {
    return todayInDz(tz || 'UTC') || '';
  } catch (_) {
    return '';
  }
}

/**
 * Does `value` have the same shape as `defaultValue`? A preference whose
 * type changed between releases (e.g. a boolean toggle that became an enum)
 * must fall back to the default rather than feed a stale shape to the page.
 * A null/undefined default accepts anything JSON can hold.
 */
export function matchesDefaultShape(value, defaultValue) {
  if (defaultValue === null || defaultValue === undefined) return value !== undefined;
  if (Array.isArray(defaultValue)) return Array.isArray(value);
  if (value === null) return false;
  if (typeof defaultValue === 'object') return typeof value === 'object' && !Array.isArray(value);
  return typeof value === typeof defaultValue;
}

function safeRemove(store, key) {
  try { store.removeItem(key); } catch (_) { /* ignore */ }
}

/**
 * Read one preference. Returns `{ has: true, value }` for a valid entry
 * written today, otherwise `{ has: false }` — removing the stale/garbled
 * entry so it is not re-examined on every render.
 */
export function readPref(storageKey, defaultValue, validate) {
  const store = storage();
  if (!storageKey || !store) return { has: false };
  let raw;
  try {
    raw = store.getItem(storageKey);
  } catch (_) {
    return { has: false };
  }
  if (raw === null || raw === undefined) return { has: false };
  let entry;
  try {
    entry = JSON.parse(raw);
  } catch (_) {
    safeRemove(store, storageKey);
    return { has: false };
  }
  const valid = entry && typeof entry === 'object'
    && Object.prototype.hasOwnProperty.call(entry, 'v')
    && typeof entry.d === 'string' && entry.d
    && entry.d === dzDayFor(entry.tz)
    && matchesDefaultShape(entry.v, defaultValue)
    && (typeof validate !== 'function' || safeValidate(validate, entry.v));
  if (!valid) {
    safeRemove(store, storageKey);
    return { has: false };
  }
  return { has: true, value: entry.v };
}

function safeValidate(validate, v) {
  try { return validate(v) === true; } catch (_) { return false; }
}

/** Persist one preference, stamped with the DZ day. False when not written. */
export function writePref(storageKey, value, tz) {
  const store = storage();
  if (!storageKey || !store || value === undefined) return false;
  if (typeof value === 'string' && value.length > MAX_PERSISTED_STRING) return false;
  const zone = tz || 'UTC';
  const d = dzDayFor(zone);
  if (!d) return false;
  try {
    store.setItem(storageKey, JSON.stringify({ v: value, d, tz: zone }));
    return true;
  } catch (_) {
    return false; // quota / disabled storage — the in-memory value still works
  }
}

export function removePref(storageKey) {
  const store = storage();
  if (storageKey && store) safeRemove(store, storageKey);
}

function allPrefKeys(store) {
  const keys = [];
  try {
    for (let i = 0; i < store.length; i += 1) {
      const k = store.key(i);
      if (k && k.startsWith(UI_PREF_PREFIX)) keys.push(k);
    }
  } catch (_) { /* ignore */ }
  return keys;
}

/** Remove every preference entry (any user) that is not from today. */
export function sweepExpiredUiPreferences() {
  const store = storage();
  if (!store) return 0;
  let removed = 0;
  for (const k of allPrefKeys(store)) {
    let keep = false;
    try {
      const e = JSON.parse(store.getItem(k));
      keep = !!(e && typeof e.d === 'string' && e.d === dzDayFor(e.tz));
    } catch (_) {
      keep = false;
    }
    if (!keep) { safeRemove(store, k); removed += 1; }
  }
  return removed;
}

/**
 * Logout cleanup. With a user id, removes THAT user's preferences (plus any
 * expired leftovers from anyone). Without one — the session's identity is
 * unknown — removes every UI preference: one browser profile holds a single
 * session at a time, so nothing under `ui:` can belong to a still-signed-in
 * account, and leaking a previous user's view is the worse failure. Never
 * touches a key outside the `ui:` namespace.
 */
export function clearUiPreferences(userId) {
  const store = storage();
  if (!store) return 0;
  const prefix = userId ? `${UI_PREF_PREFIX}${String(userId)}:` : UI_PREF_PREFIX;
  let removed = 0;
  for (const k of allPrefKeys(store)) {
    if (k.startsWith(prefix)) { safeRemove(store, k); removed += 1; }
  }
  if (userId) removed += sweepExpiredUiPreferences();
  return removed;
}

/** The signed-in user's id from the cached session payload, or null. */
export function storedSessionUserId() {
  const store = storage();
  if (!store) return null;
  try {
    const u = JSON.parse(store.getItem('skydiving_user') || 'null');
    return (u && (u.id || u._id)) ? String(u.id || u._id) : null;
  } catch (_) {
    return null;
  }
}
